import React, { useCallback, useEffect, useState } from "react";
import BreadcrumbComponent from "../common/BreadcrumbComponent";
import FormComponent from "../common/FormComponent";
import Header from "../common/Header";
import { useCountyListReducer } from "../../redux/slice/countyListSlice";
import {
  updateElementValuetemplateReducer,
  useTemplateReducer,
  defaultValue,
  createUpdateTemplate,
  resetTemplateData,
  getTemplateDataById,
} from "../../redux/slice/templateSlice";
import { useAppDispatch } from "../../hooks/hooks";
import { useLocation, useNavigate } from "react-router-dom";
import {
  PERSON_OF_HEADER,
  ROLE_COUNTY_ADMIN,
  SEARCH_WAR_TEMPLATE_TYPE_COMPLAINT,
  FIELDS_COMPLAINT_ORDER,
} from "../../constants/common";
import * as yup from "yup";
import ErrorMessageAlert, { useAlert } from "../common/Alert";
import { useLoginReducer } from "../../redux/slice/loginSlice";
import Editor from "../common/CKEditor";
import { RiInformationFill } from "react-icons/ri";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Joyride, { Step } from "react-joyride";
import StepContent from "./StepContent";
import TextInputComponent from "../common/TextInputComponent";

let defaultCountySchema = yup.object().shape({
  templateCountyId: yup.number().min(1, "County is required."),
  templateName: yup.string().required("Template Name is required."),
  // PR 11759 Start
  referenceCode: yup.string().when("templateCountyName", {
    is: (county: string) => county?.toLowerCase() === "dupage",
      then: (schema) =>
        schema
          .required("Reference Code is required.") 
          .test("len", "Reference Code must be exactly 6 characters.", (val) => {
            if (!val) return true; 
            return val.length === 6;
          }) 
          .matches(/^[a-zA-Z0-9]*$/, "Reference Code can only contain letters and numbers."), 
      otherwise: (schema) =>
        schema
          .nullable()
          .transform((val) => (val === "" ? null : val))
          .test("len", "Reference Code must be exactly 6 characters.", (val) => {
            if (!val) return true; 
            return val.length === 6;
          })
        .matches(/^[a-zA-Z0-9]*$/, "Reference Code can only contain letters and numbers."),
  }),  
  // PR 11759 End
  personOf: yup
    .string()
    .required("Person/Premises/Item information is required."),
  offenseInfo: yup.string().required("Offence is required."),
  seizedInfo: yup.string().required("Seized is required."),
  policeOfficerInfo: yup
    .string()
    .required("Probable cause / Officer background information is required."),
  pOSPrsnPremisesText: yup
    .string()
    .required("Person or premises value for Seized information is required."),
  pOSPrsnPremisesPrint: yup
    .string()
    .required("Inventory person and premises is required."),
  invntryPrsnPrmsTxt: yup
    .string()
    .required(
      "Proof of service person and premises print language is required."
    ),
  invntryPrsnPrmsPrint: yup
    .string()
    .required("Inventory person and premises print language is required."),
  personOfHeaderPrefix: yup
    .string()
    .required("Field 1 Heading Prefix is required."),
  personOfHeader: yup
    .string()
    .required("Field 1 Heading is required."),

  personOfHeaderSuffix: yup
    .string()
    .required("Field 1 Heading Suffix is required."),
  offenseInfoHeader: yup
    .string()
    .required("Field 2 Heading is required."),
  offenseInfoHeaderSuffix: yup
    .string()
    .required("Field 2 Heading Suffix is required."),
  seizedInfoHeader: yup
    .string()
    .required("Field 3 Heading is required."),
  policeOfficerInfoHeader: yup
    .string()
    .required("Field 4 Heading is required."),
  policeOfficerInfoHeaderSuffix: yup
    .string()
    .required("Field 4 Heading Suffix is required."),
  impoundLanguage: yup
    .string()
    .required("Impound Language is required."),
  complaintHeaderPrefix: yup
    .string()
    .required("Complaint Header Prefix is required."),
  warrantField1Prefix: yup
    .string()
    .required("Warrant Field 01 Prefix is required."),
  warrantOrderCommand: yup
    .string()
    .required("Warrant Order Command is required."),
  //KHL PR-11666 08/27/2025 - START
  complaintDocHeader: yup
    .string()
    .required("Complaint Header is required.")
    .max(60, "Complaint Header must be at most 60 characters."),
  swDocHeader: yup
    .string()
    .required("Warrant Header is required.")
    .max(60, "Warrant Header must be at most 60 characters."),
  //KHL PR-11666 08/27/2025 - END
});

interface ComplaintOrSearchTempProps {}

const ComplaintOrSearchTemp: React.FC<ComplaintOrSearchTempProps> = (props) => {
  const { countyList } = useCountyListReducer();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const templateReducer = useTemplateReducer();
  const [errorKeys, setErrorKeys] = useState([""]);
  const errorMessageAlert = useAlert();
  const location = useLocation();
  const { userType } = useLoginReducer();
  const [joyrideKey, setJoyrideKey] = useState(0);

  const [{ run, steps }, setState] = useState({
    run: false,
    steps: [
    ] as Step[]
  })

  // PR 11759 Start
  const [selectedCounty, setSelectedCounty] =useState<string>("");

  useEffect(() => {
    if (countyList.length > 0 && templateReducer.templateCountyId) {
      const countyName =
        countyList.find((c) => c.countyID === templateReducer.templateCountyId)?.countyName || "";
      setSelectedCounty(countyName);
    }
  }, [countyList, templateReducer.templateCountyId]);
  // PR 11759

  const guidanceHandler = (fieldName: string, label: string) => {
    let placement: 'top' | 'bottom' = "bottom";
    if (fieldName === "warrantOrderCommand" || fieldName === "overrideInventorySeizedLanguage")
      placement = 'top'
    setState(prevState => ({
      steps: [
        {
          content: `${fieldName}`,
          placement: placement,
          target: `#${fieldName}`,
          title: label,
          disableBeacon: true,
        }
      ],
      run: true
    }))
    setJoyrideKey(prev => prev + 1);
  }; 

  const handleSelectChange = (name: string) => (e: any) => {
    dispatch(
      updateElementValuetemplateReducer({
        elementName: "personOfHeader",
        value: e.Judicial + " " + PERSON_OF_HEADER,
      })
    );

    dispatch(
      updateElementValuetemplateReducer({
        elementName: name,
        value: e.value,
      })
    );
  };
  const handleCkEditorValChng = (name: any, editor: any) => {
    dispatch(
      updateElementValuetemplateReducer({
        elementName: name,
        value: editor.getData(),
      })
    );
  };
  const handleInputChange = (e: any) => {
    // PR 11759 Start
    if(e.target.name === "pOSPrsnPremisesText" && e.target.value === ""){
        e.target.value = "Person and Premises";
    }else if(e.target.name === "invntryPrsnPrmsTxt" && e.target.value === ""){
        e.target.value = "the Person and Premises";
    }else if(e.target.name === "pOSPrsnPremisesPrint" && e.target.value === ""){
        e.target.value =  "I executed this Warrant by serving the above named";
    }else if(e.target.name === "invntryPrsnPrmsPrint" && e.target.value === ""){
        e.target.value = "Inventory Person and Premises Print Language";
    }
    // PR 11759 End
    dispatch(
      updateElementValuetemplateReducer({
        elementName: e.target.name,
        value: e.target.value,
      })
    );
  };
  const handleError = useCallback(
    (_errorKeys: string[], errorMessages: string[]) => {
      setErrorKeys(_errorKeys);
      errorMessageAlert.handleError(errorMessages);
      window.scroll({ top: 0, behavior: "smooth" });
    },
    []
  );
  useEffect(() => {
    return () => {
      dispatch(resetTemplateData());
    };
  }, []);
  useEffect(() => {
    dispatch(resetTemplateData());
    dispatch(
      updateElementValuetemplateReducer({
        elementName: "templateType",
        value: SEARCH_WAR_TEMPLATE_TYPE_COMPLAINT,
      })
    );
    dispatch(defaultValue());
  }, []);
  useEffect(() => {
    const templateId = location.pathname.split("/")[2];

    if (templateId) {
      dispatch(resetTemplateData());
      dispatch(getTemplateDataById(templateId));
    }
  }, [dispatch, location, navigate]);

  useEffect(() => {
    if (templateReducer.templateError) {
      handleError([], [templateReducer.templateError]);
      dispatch(
        updateElementValuetemplateReducer({
          elementName: "templateError",
          value: "",
        })
      );
    }
  }, [templateReducer.templateError, dispatch, handleError]);

  const handleSubmit = async () => {
    try {
      await defaultCountySchema.validate({
        ...templateReducer,
         templateCountyName:selectedCounty   // PR 11759 added
        },
        {
        abortEarly: false,
      });

      let templateObj = {
        templateId: templateReducer.templateId,
        countyIdParam: templateReducer.templateCountyId,
        templateName: templateReducer.templateName,
        warrantFormName: SEARCH_WAR_TEMPLATE_TYPE_COMPLAINT,
        templateField: FIELDS_COMPLAINT_ORDER,
        referenceCode: templateReducer.referenceCode,  // PR 11759 added
        templateValue: [
          templateReducer.personOf,
          templateReducer.personOfHeader,
          templateReducer.offenseInfo,
          templateReducer.offenseInfoHeader,
          templateReducer.seizedInfo,
          templateReducer.seizedInfoHeader,
          templateReducer.policeOfficerInfo,
          templateReducer.policeOfficerInfoHeader,
          templateReducer.pOSPrsnPremisesText,
          templateReducer.invntryPrsnPrmsTxt,
          templateReducer.pOSPrsnPremisesPrint,
          templateReducer.invntryPrsnPrmsPrint,
          templateReducer.personOfHeaderPrefix,
          templateReducer.personOfHeaderSuffix,
          templateReducer.offenseInfoHeaderSuffix,
          templateReducer.policeOfficerInfoHeaderSuffix,
          templateReducer.impoundLanguage,
          templateReducer.complaintHeaderPrefix,
          templateReducer.warrantField1Prefix,
          templateReducer.warrantField2Prefix,
          templateReducer.warrantHeader3Prefix,
          templateReducer.warrantOrderCommand,
          templateReducer.complaintTitle,
          templateReducer.swTitle,
          templateReducer.impoundComplaintLanguage,
          templateReducer.overrideInventorySeizedLanguage,
          //KHL PR-11666 08/27/2025 - START
          templateReducer.complaintDocHeader,
          templateReducer.swDocHeader,
          //KHL PR-11666 08/27/2025 - END
        ],
      };

      console.log(templateObj)

      let fetchType;
      if (location.pathname.split("/")[1].includes("edit")) {
        fetchType = "PUT";
      } else {
        fetchType = "POST";
      }
      dispatch(createUpdateTemplate({ templateObj, fetchType, navigate }));
    } catch (err: any) {
      handleError(
        err.inner.map((e: any) => e.path),
        err.errors
      );
    }
  };

  let itemLinkList = [
    { itemLink: "/", itemName: "DashBoard" },
    { itemLink: "/TemplateList", itemName: "Template List" },
  ];

  const data = [
    {
      type: "selectInput",
      props: {
        options: [
          { label: "-- SELECT --", value: 0, Judicial: "" },
          ...countyList.map((county: any) => {
            return {
              label: county.countyName,
              value: county.countyID,
              Judicial: county.judicialNumberText,
            };
          }),
        ],
        value: countyList
          .map((county: any) => {
            return {
              label: county.countyName,
              value: county.countyID,
              Judicial: county.judicialNumberText,
            };
          })
          .find(
            (item) =>
              item.value ===
              (location.pathname.split("/")[2]
                ? templateReducer.templateCountyId
                : 0)
          ),
        onChange: handleSelectChange("templateCountyId"),
        placeholder: "Select County",
        name: "templateCountyId",
        defaultValue: { label: "-- SELECT --", value: 0, Judicial: "" },
        isError: errorKeys.includes("templateCountyId"),
        isDisabled:
          location.pathname.split("/")[1].includes("edit") ||
          (location.pathname.split("/")[1].includes("copy") &&
            userType === ROLE_COUNTY_ADMIN),
      },
      colSize: 4,
      label: "County",
      inLine: true,
    },

    {
      type: "textInput",
      props: {
        type: "text",
        placeholder: "Enter Template Name",
        onChange: handleInputChange,
        name: "templateName",
        value: templateReducer.templateName,
        isError: errorKeys.includes("templateName"),
        disabled: location.pathname.split("/")[1].includes("edit") || false,
      },
      colSize: 4,
      label: "Template Name",
      inLine: true,
    },
    {
      type: "textInput",
      props: {
        type: "text",
        placeholder: "Enter Template type",
        onChange: handleInputChange,
        name: "templateType",
        disabled: true,
        value: templateReducer.templateType,
      },
      colSize: 4,
      label: "Template type",
      inLine: true,
    },
    {
      type: "textInput",
      props: {
        type: "text",
        placeholder: "Enter Complaint Title",
        onChange: handleInputChange,
        name: "complaintTitle",
        value: templateReducer.complaintTitle,
      },
      colSize: 6,
      label: "Complaint Title",
    },
    {
      type: "textInput",
      props: {
        type: "text",
        placeholder: "Enter Search Warrant Title",
        onChange: handleInputChange,
        name: "swTitle",
        value: templateReducer.swTitle,
      },
      colSize: 6,
      label: "Search Warrant Title",
    },
    //KHL PR-11666 08/27/2025 - START
    {
      type: "textInput",
      props: {
        type: "text",
        placeholder: "Enter Complaint Header",
        onChange: handleInputChange,
        name: "complaintDocHeader",
        value: templateReducer.complaintDocHeader,
        isError: errorKeys.includes("complaintDocHeader")
      },
      colSize: 6,
      label: "Complaint Header",
    },
    {
      type: "textInput",
      props: {
        type: "text",
        placeholder: "Enter Search Warrant Header",
        onChange: handleInputChange,
        name: "swDocHeader",
        value: templateReducer.swDocHeader,
        isError: errorKeys.includes("swDocHeader")
      },
      colSize: 6,
      label: "Search Warrant Header",
    },
    //KHL PR-11666 08/27/2025 - END
    // PR 11759 Start
    {
      type: "textInput",
      props: {
        type: "text",
        placeholder: "Enter Reference Number",
        onChange: handleInputChange,
        name: "referenceCode",
        value: templateReducer.referenceCode,
        isError: errorKeys.includes("referenceCode"),
        maxLength: 6,
      },
      colSize: 6,
      label: "Reference Code",

    },// PR 11759 End
    {
      type: "guidedEditor",
      props: {
        data: templateReducer.personOfHeaderPrefix ? templateReducer.personOfHeaderPrefix : "",
        onChange: (event: any, editor: any) => {
          handleCkEditorValChng("personOfHeaderPrefix", editor);
        },
        isError: errorKeys.includes("personOfHeaderPrefix"),
        disabled:
        location.pathname.split("/")[1].includes("copy") &&
        userType === ROLE_COUNTY_ADMIN,
        elementLabel: "Field 1 Heading Prefix",
        name: "personOfHeaderPrefix"
      },
      colSize: 12,
    },
    {
      type: "guidedEditor",
      props: {
        data: templateReducer.personOfHeader ? templateReducer.personOfHeader : "",
        onChange: (event: any, editor: any) => {
          handleCkEditorValChng("personOfHeader", editor);
        },
        isError: errorKeys.includes("personOfHeader"),
        disabled:
        location.pathname.split("/")[1].includes("copy") &&
        userType === ROLE_COUNTY_ADMIN,
        elementLabel: "Field 1 Heading",
        name: "personOfHeader"
      },
      colSize: 12,
    },
    // {
    //   type: "textInput",
    //   props: {
    //     type: "text",
    //     placeholder: "Enter Field 1 Heading",
    //     name: "personOfHeader",
    //     onChange: handleInputChange,
    //     value: templateReducer.personOfHeader,
    //     isError: errorKeys.includes("personOfHeader"),
    //     disabled:
    //       location.pathname.split("/")[1].includes("copy") &&
    //       userType === ROLE_COUNTY_ADMIN,
    //   },
    //   colSize: 12,
    //   label: "Field 1 Heading",
    // },
    {
      type: "guidedEditor",
      props: {
        data: templateReducer.personOfHeaderSuffix ? templateReducer.personOfHeaderSuffix : "",
        onChange: (event: any, editor: any) => {
          handleCkEditorValChng("personOfHeaderSuffix", editor);
        },
        name: "personOfHeaderSuffix",
        isError: errorKeys.includes("personOfHeaderSuffix"),
        disabled:
          location.pathname.split("/")[1].includes("copy") &&
          userType === ROLE_COUNTY_ADMIN,
        elementLabel: "Field 1 Heading Suffix",
      },
      colSize: 12,
    },
    {
      type: "guidedEditor",
      props: {
        data: templateReducer.personOf ? templateReducer.personOf : "",
        onChange: (event: any, editor: any) => {
          handleCkEditorValChng("personOf", editor);
        },
        name: "personOf",
        isError: errorKeys.includes("personOf"),
        elementLabel: "Field 1 Description",
      },
      colSize: 12,
    },
    {
      type: "guidedEditor",
      props: {
        data: templateReducer.offenseInfoHeader ? templateReducer.offenseInfoHeader : "",
        onChange: (event: any, editor: any) => {
          handleCkEditorValChng("offenseInfoHeader", editor);
        },
        name: "offenseInfoHeader",
        isError: errorKeys.includes("offenseInfoHeader"),
        disabled:
        location.pathname.split("/")[1].includes("copy") &&
        userType === ROLE_COUNTY_ADMIN,
        elementLabel: "Field 2 Heading",
      },
      colSize: 12,
    },
    // {
    //   type: "textInput",
    //   props: {
    //     type: "text",
    //     placeholder: "Enter Field 2 Heading",
    //     name: "offenseInfoHeader",
    //     onChange: handleInputChange,
    //     value: templateReducer.offenseInfoHeader,
    //     isError: errorKeys.includes("offenseInfoHeader"),
    //     disabled:
    //       location.pathname.split("/")[1].includes("copy") &&
    //       userType === ROLE_COUNTY_ADMIN,
    //   },
    //   colSize: 12,
    //   label: "Field 2 Heading",
    // },
   {
    type: "guidedEditor",
    props: {
      data: templateReducer.offenseInfoHeaderSuffix ? templateReducer.offenseInfoHeaderSuffix : "",
      onChange: (event: any, editor: any) => {
        handleCkEditorValChng("offenseInfoHeaderSuffix", editor);
      },
      name: "offenseInfoHeaderSuffix",
      isError: errorKeys.includes("offenseInfoHeaderSuffix"),
      disabled:
      location.pathname.split("/")[1].includes("copy") &&
      userType === ROLE_COUNTY_ADMIN,
      elementLabel: "Field 2 Heading Suffix",
    },
    colSize: 12,
  },
    {
      type: "guidedEditor",
      props: {
        data: templateReducer.offenseInfo ? templateReducer.offenseInfo : "",
        onChange: (event: any, editor: any) => {
          handleCkEditorValChng("offenseInfo", editor);
        },
        name: "offenseInfo",
        isError: errorKeys.includes("offenseInfo"),
        elementLabel: "Field 2 Description",
      },
      colSize: 12,
    },
    {
      type: "guidedEditor",
      props: {
        data: templateReducer.seizedInfoHeader ? templateReducer.seizedInfoHeader : "",
        onChange: (event: any, editor: any) => {
          handleCkEditorValChng("seizedInfoHeader", editor);
        },
        name: "seizedInfoHeader",
        isError: errorKeys.includes("seizedInfoHeader"),
        disabled:
        location.pathname.split("/")[1].includes("copy") &&
        userType === ROLE_COUNTY_ADMIN,
        elementLabel: "Field 3 Heading",
      },
      colSize: 12,
    },
    // {
    //   type: "textInput",
    //   props: {
    //     type: "text",
    //     placeholder: "Enter Field 3 Heading",
    //     name: "seizedInfoHeader",
    //     onChange: handleInputChange,
    //     value: templateReducer.seizedInfoHeader,
    //     isError: errorKeys.includes("seizedInfoHeader"),
    //     disabled:
    //       location.pathname.split("/")[1].includes("copy") &&
    //       userType === ROLE_COUNTY_ADMIN,
    //   },
    //   colSize: 12,
    //   label: "Field 3 Heading",
    // },
    {
      type: "guidedEditor",
      props: {
        data: templateReducer.seizedInfo ? templateReducer.seizedInfo : "",
        onChange: (event: any, editor: any) => {
          handleCkEditorValChng("seizedInfo", editor);
        },
        name: "seizedInfo",
        isError: errorKeys.includes("seizedInfo"),
        elementLabel: "Field 3 Description",
      },
      colSize: 12,
    },
    {
      type: "guidedEditor",
      props: {
        data: templateReducer.policeOfficerInfoHeader ? templateReducer.policeOfficerInfoHeader : "",
        onChange: (event: any, editor: any) => {
          handleCkEditorValChng("policeOfficerInfoHeader", editor);
        },
        name: "policeOfficerInfoHeader",
        isError: errorKeys.includes("policeOfficerInfoHeader"),
        disabled:
        location.pathname.split("/")[1].includes("copy") &&
        userType === ROLE_COUNTY_ADMIN,
        elementLabel: "Field 4 Heading",
      },
      colSize: 12,
    },
    // {
    //   type: "textInput",
    //   props: {
    //     type: "text",
    //     placeholder: "Enter Field 4 Heading",
    //     name: "policeOfficerInfoHeader",
    //     onChange: handleInputChange,
    //     value: templateReducer.policeOfficerInfoHeader,
    //     isError: errorKeys.includes("policeOfficerInfoHeader"),
    //     disabled:
    //       location.pathname.split("/")[1].includes("copy") &&
    //       userType === ROLE_COUNTY_ADMIN,
    //   },
    //   colSize: 12,
    //   label: "Field 4 Heading",
    // },
    {
      type: "guidedEditor",
      props: {
        data: templateReducer.policeOfficerInfoHeaderSuffix ? templateReducer.policeOfficerInfoHeaderSuffix : "",
        onChange: (event: any, editor: any) => {
          handleCkEditorValChng("policeOfficerInfoHeaderSuffix", editor);
        },
        name: "policeOfficerInfoHeaderSuffix",
        isError: errorKeys.includes("policeOfficerInfoHeaderSuffix"),
        disabled:
          location.pathname.split("/")[1].includes("copy") &&
          userType === ROLE_COUNTY_ADMIN,
        elementLabel: "Field 4 Heading Suffix",
      },
      colSize: 12,
    },
    {
      type: "guidedEditor",
      props: {
        data: templateReducer.policeOfficerInfo
          ? templateReducer.policeOfficerInfo
          : "",
        onChange: (event: any, editor: any) => {
          handleCkEditorValChng("policeOfficerInfo", editor);
        },
        name: "policeOfficerInfo",
        isError: errorKeys.includes("policeOfficerInfo"),
        elementLabel: "Field 4 Description",
      },
      colSize: 12,
    },
    {
      type: "guidedEditor",
      props: {
        data: templateReducer.impoundLanguage
          ? templateReducer.impoundLanguage
          : "",
        onChange: (event: any, editor: any) => {
          handleCkEditorValChng("impoundLanguage", editor);
        },
        name: "impoundLanguage",
        isError: errorKeys.includes("impoundLanguage"),
        elementLabel: "Impound Order Language",
      },
      colSize: 12,
    },
    {
      type: "guidedTextInput",
      props: {
        type: "text",
        placeholder: "Enter Proof of service Person and Premises",
        name: "pOSPrsnPremisesText",
        onChange: handleInputChange,
        value: templateReducer.pOSPrsnPremisesText?templateReducer.pOSPrsnPremisesText:"Person and Premises",
        isError: errorKeys.includes("pOSPrsnPremisesText"),
        elementLabel: "Proof of service Person and Premises",
      },
      colSize: 6,
    },
    {
      type: "guidedTextInput",
      props: {
        type: "text",
        placeholder:
          "Enter Proof of service Person and Premises Print Language",
        name: "pOSPrsnPremisesPrint",
        onChange: handleInputChange,
        value: templateReducer.pOSPrsnPremisesPrint?templateReducer.pOSPrsnPremisesPrint:"I executed this Warrant by serving the above named",
        isError: errorKeys.includes("pOSPrsnPremisesPrint"),
        elementLabel: "Proof of service Person and Premises Print Language",
      },
      colSize: 6,
    },
    {
      type: "guidedTextInput",
      props: {
        type: "text",
        placeholder: "Enter Inventory Person and Premises",
        name: "invntryPrsnPrmsTxt",
        onChange: handleInputChange,
        value: templateReducer.invntryPrsnPrmsTxt?templateReducer.invntryPrsnPrmsTxt:"the Person and Premises",
        isError: errorKeys.includes("invntryPrsnPrmsTxt"),
        elementLabel: "Inventory Person and Premises",
      },
      colSize: 6,
    },
    {
      type: "guidedTextInput",
      props: {
        type: "text",
        placeholder: "Enter Inventory Person and Premises Print Language",
        name: "invntryPrsnPrmsPrint",
        onChange: handleInputChange,
        value: templateReducer.invntryPrsnPrmsPrint?templateReducer.invntryPrsnPrmsPrint:"Inventory Person and Premises Print Language",
        isError: errorKeys.includes("invntryPrsnPrmsPrint"),
        elementLabel: "Inventory Person and Premises Print Language",
      },
      colSize: 6,
    },
    {
      type: "guidedEditor",
      props: {
        data: templateReducer.complaintHeaderPrefix ? templateReducer.complaintHeaderPrefix : "",
        onChange: (event: any, editor: any) => {
          handleCkEditorValChng("complaintHeaderPrefix", editor);
        },
        name: "complaintHeaderPrefix",
        isError: errorKeys.includes("complaintHeaderPrefix"),
        disabled:
          location.pathname.split("/")[1].includes("copy") &&
          userType === ROLE_COUNTY_ADMIN,
        elementLabel: "Complaint header Prefix",
      },
      colSize: 12,
    },
    {
      type: "guidedEditor",
      props: {
        data: templateReducer.warrantField1Prefix ? templateReducer.warrantField1Prefix : "",
        onChange: (event: any, editor: any) => {
          handleCkEditorValChng("warrantField1Prefix", editor);
        },
        name: "warrantField1Prefix",
        isError: errorKeys.includes("warrantField1Prefix"),
        disabled:
          location.pathname.split("/")[1].includes("copy") &&
          userType === ROLE_COUNTY_ADMIN,
        elementLabel: "Warrant Field 01 Prefix",
      },
      colSize: 12,
    },
    {
      type: "guidedEditor",
      props: {
        data: templateReducer.warrantField2Prefix ? templateReducer.warrantField2Prefix : "",
        onChange: (event: any, editor: any) => {
          handleCkEditorValChng("warrantField2Prefix", editor);
        },
        name: "warrantField2Prefix",
        disabled:
          location.pathname.split("/")[1].includes("copy") &&
          userType === ROLE_COUNTY_ADMIN,
        elementLabel: "Warrant Field 02 Prefix",
      },
      colSize: 12,
    },
    {
      type: "guidedEditor",
      props: {
        data: templateReducer.warrantHeader3Prefix ? templateReducer.warrantHeader3Prefix : "",
        onChange: (event: any, editor: any) => {
          handleCkEditorValChng("warrantHeader3Prefix", editor);
        },
        name: "warrantHeader3Prefix",
        disabled:
          location.pathname.split("/")[1].includes("copy") &&
          userType === ROLE_COUNTY_ADMIN,
        elementLabel: "Warrant Field 03 Prefix",
      },
      colSize: 12,
    },
    {
      type: "guidedEditor",
      props: {
        data: templateReducer.impoundComplaintLanguage
          ? templateReducer.impoundComplaintLanguage
          : "",
        onChange: (event: any, editor: any) => {
          handleCkEditorValChng("impoundComplaintLanguage", editor);
        },
        name: "impoundComplaintLanguage",
        elementLabel: "Impound Complaint Language",
      },
      colSize: 12,
    },
    {
      type: "guidedEditor",
      props: {
        data: templateReducer.warrantOrderCommand
          ? templateReducer.warrantOrderCommand
          : "",
        onChange: (event: any, editor: any) => {
          handleCkEditorValChng("warrantOrderCommand", editor);
        },
        name: "warrantOrderCommand",
        isError: errorKeys.includes("warrantOrderCommand"),
        elementLabel: "Warrant Order Command",
      },
      colSize: 12,
    },
    {
      type: "guidedEditor",
      props: {
        data: templateReducer.overrideInventorySeizedLanguage ? templateReducer.overrideInventorySeizedLanguage : "",
        onChange: (event: any, editor: any) => {
          handleCkEditorValChng("overrideInventorySeizedLanguage", editor);
        },
        isError: errorKeys.includes("overrideInventorySeizedLanguage"),
        elementLabel: "Override Language for \"Items to be Seized\" in Inventory",
        name: "overrideInventorySeizedLanguage"
      },
      colSize: 12,
    },
    {
      type: "button",
      props: {
        children: location.pathname.split("/")[1].includes("edit")
          ? "Update"
          : "Save",
        className: "btn-primary ms-2",
        onClick: handleSubmit,
      },
    },
    {
      type: "button",
      props: {
        children: "Cancel",
        className: "btn-danger ms-2",
        onClick: () => {
          navigate(-1);
        },
      },
    },
  ];

  return (
    <>
      <Header headerName="Complaint Order" />
      <div className="main-body p-3">
        <BreadcrumbComponent
          itemList={itemLinkList}
          itemActive="Complaint Order"
        />
        <ErrorMessageAlert
          messages={errorMessageAlert.messages}
          clearError={errorMessageAlert.clearError}
        />
        <Joyride
          callback={() => { }}
          key={joyrideKey}
          run={run}
          steps={steps}
          hideCloseButton
          scrollToFirstStep={true}
          showSkipButton
          showProgress
          continuous={true}
          disableOverlayClose={true}
          spotlightClicks={false}

          tooltipComponent={StepContent}
          floaterProps={{
            disableFlip: true,
            disableAnimation: true,
            styles: {
              floater: {
                position: "fixed"
              }
            }
          }}
        />
        <FormComponent
          totalColumns={12}
          colSize={6}
          componentData={data}
          subHeader="Complaint Order Form"
          components = {[
            {
              type: "guidedEditor",
              node: (props: any) => (
                <div 
                  id={props?.name}
                >
                  <OverlayTrigger overlay={<Tooltip id="tooltip-disabled">Click to preview this field's placement on the UI and PDF.</Tooltip>} placement={'auto-end'}>
                    <span onClick={()=>guidanceHandler(props?.name, props.elementLabel)}>
                    {props?.elementLabel} <RiInformationFill className="guided-editor-icon"/>
                    </span>
                  </OverlayTrigger>
                  <Editor {...props}>{props.children}</Editor>
                </div>
              ),
            },
            {
              type: "guidedTextInput",
              node: (props: any) => (
                <div 
                  id={props?.name}
                >
                  <OverlayTrigger overlay={<Tooltip id="tooltip-disabled">Click to preview this field's placement on the UI and PDF.</Tooltip>} placement={'auto-end'}>
                    <label className="label-with-icon" onClick={()=>guidanceHandler(props?.name, props.elementLabel)}>
                    {props?.elementLabel} <RiInformationFill className="guided-editor-icon"/>
                    </label>
                  </OverlayTrigger>
                  <TextInputComponent {...props} />
                </div>
              ),
            }
          ]}
        />
      </div>
    </>
  );
};

export default ComplaintOrSearchTemp;
